import {
	CONNECTED,
	INITIALIZING,
	INITIALIZATION_FAILED,
	desktopNotification,
	playAudio,
	toggleState
} from "./comms";

import {
	where,
	orderBy,
	documentId,
	startAt,
	startAfter,
	endBefore,
	endAt,
	limit as queryLimit,
	limitToLast,
	Timestamp
} from "firebase/firestore";
import { subscribeToCol, unsubscribeCollection } from "@/composables/events/handler";
import {
	fetchConversationBySid,
	fetchConversationPageOf,
	fetchConversationsLastPage
} from "@/services/conversationService";
import { EXTENSION_TO_MIME } from "@/common/utils";
import { debounce } from "lodash";
import Vue from "vue";
import axios from "axios";
import store from "../store";
import { MessageAuthor } from "@/common/const";

// ---- Module-level state ----

const conversationsMap = new Map();

let messageWatcherUnsub = null;
let messageSubscription = null;

let pageSubscription = null;
let currentState = "active";
let currentLimit = 20;

const MAX_ATTEMPTS = 5;

// The unread badge has its own capped query, independent of the list order.
// The list renders anything at or past this cap as "50+".
export const UNREAD_DISPLAY_CAP = 50;

// First messages page size: served from the selected conversation's realtime
// snapshot; older pages are fetched via REST (fetchMoreMessages).
const MESSAGES_PAGE = 15;

const realtimeKey = (conversationState) => `conversations-v2:${conversationState}`;

// Bumped on every cleanup (e.g. user-group switch). Async page/count requests
// capture the generation at start and must not commit their result if it changed
// while they were in flight, otherwise a stale request could repopulate the
// released store/map with the previous group's data.
let loadGeneration = 0;

const unreadListenerOpen = { active: false, inactive: false };

const pageNav = {
	active: { pageIndex: 0, firstCursor: null, lastCursor: null },
	inactive: { pageIndex: 0, firstCursor: null, lastCursor: null }
};

let pageLoadInFlight = false;

const conversationCursor = (data) => [
	!!data.pinned,
	data.updatedAt?.toDate ? data.updatedAt : Timestamp.fromDate(new Date(data.updatedAt)),
	data.id
];

const inferMediaContentType = (url) => {
	const extension = (url.split("?")[0].split(".").pop() || "").toLowerCase();

	return EXTENSION_TO_MIME[extension];
};

// Resolves a stored media reference to a displayable URL, as { url, expiresAt }.
// Bare filenames live in private storage: the backend verifies the caller's
// access to the conversation, then returns a short-lived signed GCS URL, so the
// browser streams the object directly (range requests, progressive audio
// playback) instead of downloading base64 through the API. Full URLs (legacy
// V1/Twilio media) are returned as-is. The message `type` is forwarded so the
// backend signs the correct private folder (call recordings vs text/media
// attachments), since V2 keeps every reference under `message.medias`.
// Rejects when minting fails so consumers can run their error flow (retry cap,
// toast, placeholder); resolving to a fallback URL would silently pass a broken
// reference to the audio players.
const resolveMediaTemporaryUrl = async (media, conversationSid, type) => {
	// Absolute URLs are legacy Twilio/V1 references — usable directly, forever.
	if (/^https?:\/\//i.test(media)) {
		return { url: media, expiresAt: Infinity };
	}

	const { data } = await axios.get(
		`conversations/v2/medias/${conversationSid}/${encodeURIComponent(media)}/url`,
		{
			params: type ? { type } : undefined
		}
	);

	return data;
};

// ---- Conversation Message entity ----
class ConversationMessage {
	constructor(data, docId) {
		this.ver = 2;
		
		this.id = docId;
		this.sid = data.sid;
		this.userId = data.userId;
		this.userName = data.userName;
		this.conversationSid = data.conversationSid;
		
		this.author = data.author;
		this.body = data.body;
		this.type = data.type;
		this.direction = data.direction === 1 ? "inbound" : "outbound";
		this.deliveryStatus = data.deliveryStatus;
		this.sentiment = data.sentiment == null ? "Neutral" : (data.sentiment ? "Positive" : "Negative");
		this.medias = data.medias;
		this.provider = data.provider;
		this.status = data.status;
		
		// Object related id like callSid, email id...
		this.objectId = data.objectId;

		// Inline V2 transcription: locale-keyed text map (e.g. { "fr-CA": "..." }),
		// written by the live transcription flow and the Twilio migration.
		this.transcription = data.transcription;
		// Inline V2 voicemail summary: { status, summary[], labels[], priority, ... }
		// written by the async summary listener.
		this.summary = data.summary;
		this.subject = data.subject;
		this.dateCreated = data.createdAt?.toDate
			? data.createdAt.toDate()
			: new Date(data.createdAt);
		this.dateUpdated = data.updatedAt?.toDate
			? data.updatedAt.toDate()
			: new Date(data.updatedAt);
		this.deliveredAt = data.deliveredAt?.toDate
			? data.deliveredAt.toDate()
			: data.deliveredAt
				? new Date(data.deliveredAt)
				: null;

		this.index = this.dateCreated.getTime();
		
		this.attributes = {
			...{
				// V1 compatibility shim:
				...(this.author === MessageAuthor.AutoResponse ? { isAutoResponse: true } : {}),
				...(this.userName?.length ? { responsibleUserName: this.userName } : {}),
				...(this.medias?.length ? { medias: this.medias } : {}),
				objectId: this.objectId,
				type: this.type,
				subject: this.subject,
				sentiment:this.sentiment,
				createdAt: this.dateCreated,
				deliveredAt: this.deliveredAt,
				direction: this.direction,
				status: this.status
			},
			...(data.attributes || {})
		};

		if (this.medias?.length) {
			const conversationSid = this.conversationSid;
			const messageType = this.type;

			this.attachedMedia = this.medias.filter(Boolean).map((media, index) => {
				// Signed URL fetched lazily (only when a component asks for it) and
				// memoized until shortly before it expires, then re-signed on demand.
				let pendingUrl;
				let expiresAt;

				return {
					contentType: inferMediaContentType(media),
					filename: this.attributes?.mediaFilenames?.[index] || `media-${index + 1}`,
					getContentTemporaryUrl: (forceRefresh = false) => {
						// One-minute safety margin so a URL is never handed out right
						// before expiring mid-download/playback. forceRefresh bypasses the
						// memo after a playback error (e.g. URL expired mid-session).
						if (forceRefresh || !pendingUrl || Date.now() > expiresAt - 60 * 1000) {
							const request = resolveMediaTemporaryUrl(media, conversationSid, messageType)
								.then((resolved) => {
									expiresAt = resolved.expiresAt;

									return resolved.url;
								})
								.catch((error) => {
									// Never memoize a failed mint: the next call retries.
									if (pendingUrl === request) pendingUrl = undefined;

									throw error;
								});

							pendingUrl = request;
						}

						return pendingUrl;
					}
				};
			});

			// Keep single-media backward compatibility
			this.media = this.attachedMedia[0];
		}

	}

	// V1 Twilio compatibility: views read message.state.attributes.*, message.state.sid, etc.
	/** @deprecated V1 compatibility shim — use top-level properties instead */
	get state() {
		return {
			sid: this.sid,
			body: this.body,
			subject: this.subject,
			author: this.author,
			media: this.media,
			index: this.index,
			type: this.type,
			direction: this.direction,
			status: this.status,
			sentiment: this.sentiment,
			version: 2,
			attributes: this.attributes
		};
	}

	/** @deprecated V1 compatibility shim — use top-level properties this.conversationSid instead  */
	get conversation() {
		return {
			sid: this.conversationSid
		};
	}

	/**
	 * Create a ConversationMessage from a backend REST API response.
	 */
	static fromApi(data) {
		return new ConversationMessage(data, data.id);
	}
}

// ---- Conversation entity ----

class Conversation {
	constructor(data) {
		this._data = data;
		this._eventHandlers = {};
		this._state = data.state;

		this.id = data.id;
		this.sid = data.sid; // for simplicity of refactoring, we use the same id as sid
		this.conversationSid = data.sid; // real generated ConversationSid from backend, link with messages
		this.friendlyName = `+${data.phoneNumber}`;

		this.unread = data.unread;
		this.pinned = data.pinned;
		this.userId = data.userId;
		this.sentimentScore = data.sentimentScore ?? 50;

		// Denormalized last non-auto-response message written by the backend on
		// every message: lets ConversationCard render its preview with no fetch.
		this.lastMessage = data.lastMessage ?? null;
		
		this.dateCreated = data.createdAt?.toDate
			? data.createdAt.toDate()
			: new Date(data.createdAt);
		this.dateUpdated = data.updatedAt?.toDate
			? data.updatedAt.toDate()
			: new Date(data.updatedAt);
	}

	/** @deprecated V1 compatibility shim — use _state directly or check conversation state via dedicated methods */
	// V1 Twilio compatibility: views read conversation.state.current
	get state() {
		return { current: this._state };
	}

	/** @deprecated V1 compatibility shim — use _state directly or check conversation state via dedicated methods */
	// V1 Twilio compatibility: views read conversation.participants.size
	get participants() {
		return { size: 2 };
	}

	/** @deprecated V1 compatibility shim — use unread, pinned, userId, sentimentScore directly */
	// V1 Twilio compatibility: views read conversation.attributes.hasNotification, .pinned, .assigned, etc.
	get attributes() {
		return {
			hasNotification: this.unread,
			pinned: this.pinned,
			assigned: this.userId,
			sentimentScore: this.sentimentScore
		};
	}

	/** @deprecated V1 compatibility shim — use top-level properties directly */
	// V1 Twilio compatibility: ContactInfo.vue reads channelState.attributes.sentimentScore, channelState.dateCreated
	get channelState() {
		return {
			dateCreated: this.dateCreated,
			attributes: {
				sentimentScore: this.sentimentScore,
				hasNotification: this.unread,
				pinned: this.pinned,
				assigned: this.userId
			}
		};
	}

	update(newData) {
		const updateReasons = [];

		const newUpdated = (newData.updatedAt?.toDate
			? newData.updatedAt.toDate()
			: new Date(newData.updatedAt)).getTime();
		const oldUpdated = (this._data.updatedAt?.toDate
			? this._data.updatedAt.toDate()
			: new Date(this._data.updatedAt)).getTime();

		if (newUpdated !== oldUpdated) {
			this.dateUpdated = newData.updatedAt?.toDate
				? newData.updatedAt.toDate()
				: new Date(newData.updatedAt);
			updateReasons.push("dateUpdated");
		}

		const newState = newData.state || "active";
		const newUnread = newData.unread ?? false;
		const newPinned = newData.pinned ?? false;
		const newUserId = newData.userId ?? undefined;
		const newSentimentScore = newData.sentimentScore ?? 50;
		const newPreview = newData.lastMessage ?? null;
		const newFriendlyName = `+${newData.phoneNumber}`;

		if (this.sentimentScore !== newSentimentScore) {
			console.debug("change sentiment score", this.sentimentScore, "=>", newSentimentScore);
			this.sentimentScore = newSentimentScore;
			updateReasons.push("sentimentScore");
		}

		if (this.unread !== newUnread) {
			console.debug("change unread", this.unread, "=>", newUnread);
			this.unread = newUnread;
			updateReasons.push("unread");
		}
		
		if (this.pinned !== newPinned) {
			console.debug("change pinned", this.pinned, "=>", newPinned);
			this.pinned = newPinned;
			updateReasons.push("pinned");
		}

		if (this.userId !== newUserId) {
			console.debug("change userId", this.userId, "=>", newUserId);
			this.userId = newUserId;
			updateReasons.push("userId");
		}

		if ((newPreview?.sid ?? null) !== (this.lastMessage?.sid ?? null)) {
			console.debug("change lastMessage", this.lastMessage?.sid, "=>", newPreview?.sid);
			this.lastMessage = newPreview;
			updateReasons.push("lastMessage");
		}

		if (this._state !== newState) {
			console.debug("change state", this._state, "=>", newState);
			this._state = newState;
			updateReasons.push("state");
		}

		// Rare but not immutable: modifyPatientPhoneNumber re-points a conversation
		// to a new phone number while preserving its history.
		if (this.friendlyName !== newFriendlyName) {
			console.debug("change friendlyName", this.friendlyName, "=>", newFriendlyName);
			this.friendlyName = newFriendlyName;
			updateReasons.push("friendlyName");
		}

		this._data = newData;

		// Centralized notification: every update path (realtime snapshot, REST page
		// reload, …) notifies listeners (e.g. visible ConversationCards) from here.
		if (updateReasons.length) {
			this.emit("updated", { conversation: this, updateReasons });
		}

		return updateReasons;
	}

	on(event, handler) {
		if (!this._eventHandlers[event]) this._eventHandlers[event] = [];
		this._eventHandlers[event].push(handler);
	}

	off(event, handler) {
		if (!this._eventHandlers[event]) return;
		this._eventHandlers[event] = this._eventHandlers[event].filter(h => h !== handler);
	}

	emit(event, ...args) {
		if (this._eventHandlers[event]) {
			this._eventHandlers[event].forEach(h => h(...args));
		}
	}
}

export { conversationsMap };

/**
 * Create a Conversation from a backend/Firestore payload and store it in the
 * shared map. Returns the cached instance, updating it in place when it already
 * exists so existing references stay valid.
 */
export const upsertConversation = (data) => {
	if (!data?.sid) return undefined;

	const existing = conversationsMap.get(data.sid);

	if (existing) {
		existing.update(data);
		return existing;
	}

	// Vue 2 cannot track mutations on plain class instances living outside the
	// store. Make each Conversation observable so realtime updates (unread,
	// pinned, userId, …) re-render the views that bind to it.
	const conv = Vue.observable(new Conversation(data));

	conversationsMap.set(conv.sid, conv);

	return conv;
};

/**
 * Ensure a conversation is available locally, fetching it by SID from the
 * backend when it is not part of the currently loaded page (e.g. restoring the
 * last opened conversation).
 */
export const ensureConversationLoaded = async (sid) => {
	if (!sid) return undefined;

	const cached = conversationsMap.get(sid);

	if (cached && pageSubscription?.seeded && pageSubscription.sids.includes(sid)) return cached;

	const generation = store.getters["conversation/selectionGeneration"];
	const data = await fetchConversationBySid(sid);

	if (generation !== store.getters["conversation/selectionGeneration"]) return undefined;

	if (!data?.sid) conversationsMap.delete(sid);

	return data?.sid ? upsertConversation(data) : undefined;
};

// ---- Composable ----

const useConversation = () => {

	// ---- Notifications ----

	// Leading-only: several conversations turning unread in one snapshot fire
	// one onAdded each, but the user should get a single sound/notification.
	const notifyIncoming = debounce(() => {
		playAudio();
		desktopNotification();
	}, 500, { leading: true, trailing: false });

	// ---- Message subscription for selected conversation ----

	const unsubscribeFromConversationMessages = () => {
		messageSubscription = null;
		unsubscribeCollection("conversation-messages");
	};

	const subscribeToConversationMessages = (conversationId) => {
		if (conversationId && messageSubscription?.sid === conversationId &&
			!store.getters["conversation/loadingMessagesError"]) return;

		unsubscribeFromConversationMessages();

		if (!conversationId) return;

		const conv = conversationsMap.get(conversationId);
		const userGroupId = store.getters["login/userGroupId"];
		const organizationId = store.getters["login/profile"]?.clinic;

		if (!userGroupId || !organizationId || !conv?.conversationSid) return;

		const subscription = { sid: conversationId };
		const isCurrentSubscription = () => messageSubscription === subscription &&
			store.getters["conversation/singleConversation"]?.sid === conversationId;

		messageSubscription = subscription;
		store.commit("conversation/setLoadingMessages", true);

		// Live history only: archived conversations (merged away by a phone
		// change) are read on demand through the archivedSid link carried by their
		// MessageMerging message — never by this listener.
		subscribeToCol("conversation-messages", [
			where("conversationSid", "==", conv.conversationSid),
			where("userGroupId", "==", userGroupId),
			where("organizationId", "==", organizationId),
			orderBy("createdAt", "desc"),
			queryLimit(MESSAGES_PAGE)
		], {
			waitForServer: true,
			// The snapshot delivers the first messages page (createdAt desc →
			// reversed to chronological) — no initial REST fetch. Older pages are
			// loaded on scroll via fetchMoreMessages (REST cursor).
			onInitialLoad: (snapshot) => {
				if (!isCurrentSubscription()) return;

				const items = snapshot.docs
					.map((doc) => new ConversationMessage(doc.data(), doc.id))
					.reverse();

				store.commit("conversation/setMessagePagination", {
					hasMore: snapshot.size >= MESSAGES_PAGE,
					nextCursor: items[0]
						? `${items[0].dateCreated.toISOString()}|${items[0].id}`
						: null
				});
				store.commit("conversation/setMessages", items);
				store.commit("conversation/setLoadingMessagesError", false);
				store.commit("conversation/setLoadingMessages", false);
			},
			onAdded: (docData, docId) => {
				if (!isCurrentSubscription()) return;

				const message = new ConversationMessage(docData, docId);

				conv.emit("messageAdded", message);
				store.commit("conversation/addNewMessage", message);
			},
			onUpdated: (docData, docId) => {
				if (!isCurrentSubscription()) return;

				const message = new ConversationMessage(docData, docId);

				store.commit("conversation/updateSingleMessage", message);
			},
			onRemoved: (docData, docId, snapshot) => {
				if (!isCurrentSubscription()) return;

				// The limit()ed listener also emits "removed" when a new message
				// evicts the oldest doc from its window; deleting that sid would
				// punch a permanent hole in the loaded history (the pagination
				// cursor already points past it). On an eviction the window stays
				// full and every remaining doc is newer than the removed one; a
				// real deletion either shrinks the window or backfills it with an
				// older doc. Ties are treated as evictions (stale ghost beats gap).
				const removedAt = docData.createdAt?.toMillis
					? docData.createdAt.toMillis()
					: new Date(docData.createdAt).getTime();
				const oldestAt = snapshot.docs[snapshot.size - 1]?.data().createdAt?.toMillis?.() ?? 0;

				if (snapshot.size >= MESSAGES_PAGE && oldestAt >= removedAt) return;

				store.commit("conversation/removeMessage", docData.sid);
			},
			onError: (error) => {
				if (!isCurrentSubscription()) return;

				messageSubscription = null;
				console.error("[CONVERSATION-V2] Firestore message snapshot error:", error);

				store.commit("conversation/setLoadingMessagesError", true);
				store.commit("conversation/setLoadingMessages", false);
			}
		});
	};

	// ---- Page loading ----

	const stateType = (conversationState) =>
		(conversationState === "active" ? "active" : "inactive");

	const subscribeUnreadRealtime = (conversationState, userGroupId, organizationId, attempt = 0) => {
		unreadListenerOpen[conversationState] = true;

		let seeded = false;
		let previousBoundary = null;

		const generation = loadGeneration;
		const commitCount = (snapshot) => {
			if (generation !== loadGeneration) return false;

			attempt = 0;
			store.commit("conversation/setUnreadCount", {
				type: stateType(conversationState),
				count: Math.min(snapshot.size, UNREAD_DISPLAY_CAP)
			});

			return true;
		};

		subscribeToCol("conversations-v2", [
			where("userGroupId", "==", userGroupId),
			where("organizationId", "==", organizationId),
			where("state", "==", conversationState),
			where("unread", "==", true),
			orderBy("updatedAt", "desc"),
			queryLimit(UNREAD_DISPLAY_CAP)
		], {
			waitForServer: true,
			onSnapshot: (snapshot) => {
				if (!commitCount(snapshot)) return;

				const hasNewUnread = seeded && snapshot.docChanges().some(({ type, doc }) => {
					if (type !== "added") return false;

					const data = doc.data();

					return !previousBoundary || data.updatedAt > previousBoundary.updatedAt ||
						(data.updatedAt.isEqual(previousBoundary.updatedAt) && doc.id > previousBoundary.id);
				});
				const oldest = snapshot.docs[snapshot.size - 1];

				previousBoundary = snapshot.size >= UNREAD_DISPLAY_CAP
					? { id: oldest.id, updatedAt: oldest.data().updatedAt }
					: null;
				seeded = true;

				if (hasNewUnread) notifyIncoming();
			},
			onError: (error) => {
				if (generation !== loadGeneration) return;

				console.error(`[CONVERSATION-V2] Firestore ${conversationState} unread snapshot error:`, error);

				if (attempt < MAX_ATTEMPTS) {
					setTimeout(() => {
						if (generation === loadGeneration) subscribeUnreadRealtime(conversationState, userGroupId, organizationId, attempt + 1);
					}, 1000 * (attempt + 1));
				} else {
					unreadListenerOpen[conversationState] = false;
					toggleState(INITIALIZATION_FAILED);
				}
			}
		}, `${realtimeKey(conversationState)}:unread`);
	};

	const ensureUnreadSubscription = (conversationState) => {
		if (unreadListenerOpen[conversationState]) return;

		const userGroupId = store.getters["login/userGroupId"];
		const organizationId = store.getters["login/profile"]?.clinic;

		if (userGroupId && organizationId) {
			subscribeUnreadRealtime(conversationState, userGroupId, organizationId);
		}
	};

	const unsubscribeFromConversations = () => {
		const subscription = pageSubscription;

		pageSubscription = null;
		subscription?.resolve(-1);
		unsubscribeCollection("conversations-v2:page");
		pageLoadInFlight = false;
		store.commit("conversation/setLoadingAllConversations", false);
	};

	const showPage = async (conversationState, pageIndex, limit, position = {}, locatePage) => {
		unsubscribeFromConversations();

		const userGroupId = store.getters["login/userGroupId"];
		const organizationId = store.getters["login/profile"]?.clinic;
		const generation = loadGeneration;

		if (!userGroupId || !organizationId) return -1;

		currentState = conversationState;
		currentLimit = limit;
		pageLoadInFlight = true;
		store.commit("conversation/setLoadingAllConversations", true);

		const subscription = { conversationState, pageIndex, limit, position, seeded: false };
		const ready = new Promise((resolve) => {
			subscription.resolve = resolve;
		});
		const isCurrentSubscription = () =>
			generation === loadGeneration && pageSubscription === subscription;
		const finishLoading = (result) => {
			if (!isCurrentSubscription()) return;

			pageLoadInFlight = false;
			store.commit("conversation/setLoadingAllConversations", false);
			subscription.resolve(result);
		};

		pageSubscription = subscription;
		ensureUnreadSubscription(conversationState);

		const subscribePage = (attempt = 0) => {
			if (!isCurrentSubscription()) return;

			const { after, before, at, through } = subscription.position;
			const backward = !!(before || through);
			const constraints = [
				where("userGroupId", "==", userGroupId),
				where("organizationId", "==", organizationId),
				where("state", "==", conversationState),
				orderBy("pinned", "desc"),
				orderBy("updatedAt", "desc"),
				orderBy(documentId(), "desc")
			];

			if (after) constraints.push(startAfter(...after));
			if (before) constraints.push(endBefore(...before));
			if (at) constraints.push(startAt(...at));
			if (through) constraints.push(endAt(...through));

			constraints.push(backward ? limitToLast(limit + 1) : queryLimit(limit + 1));

			subscribeToCol("conversations-v2", constraints, {
				// An unvisited page's cache-only snapshot holds whatever is already
				// cached past the cursor (mostly the unread docs): a wrong page that
				// would flash until the server snapshot lands.
				waitForServer: true,
				onSnapshot: (snapshot) => {
					if (!isCurrentSubscription()) return;

					attempt = 0;

					const docs = backward ? snapshot.docs.slice(-limit) : snapshot.docs.slice(0, limit);

					const sids = docs
						.map((doc) => upsertConversation({ ...doc.data(), id: doc.id })?.sid)
						.filter(Boolean);
					const first = docs[0];
					const last = docs[docs.length - 1];

					pageNav[conversationState] = {
						pageIndex: subscription.pageIndex,
						firstCursor: first ? conversationCursor({ ...first.data(), id: first.id }) : null,
						lastCursor: last ? conversationCursor({ ...last.data(), id: last.id }) : null
					};

					store.commit("conversation/setPage", {
						type: stateType(conversationState),
						sids,
						hasNext: backward ? true : snapshot.size > limit,
						hasPrev: backward && docs.length ? snapshot.size > limit : subscription.pageIndex > 0
					});

					subscription.sids = sids;
					subscription.seeded = true;
					finishLoading(subscription.pageIndex);
				},
				onError: (error) => {
					if (!isCurrentSubscription()) return;

					console.error(`[CONVERSATION-V2] Firestore ${conversationState} page snapshot error:`, error);

					if (attempt < MAX_ATTEMPTS) {
						setTimeout(() => subscribePage(attempt + 1), 1000 * (attempt + 1));
					} else {
						finishLoading(-1);
						toggleState(INITIALIZATION_FAILED);
					}
				}
			}, "conversations-v2:page");
		};

		try {
			if (locatePage) {
				const page = await locatePage();

				if (!isCurrentSubscription()) return -1;

				if (!page) {
					finishLoading(-1);
					return ready;
				}

				subscription.pageIndex = page.pageIndex ?? 0;

				if (subscription.pageIndex > 0 && !page.startCursor) {
					throw new Error("Missing conversation page cursor");
				}

				subscription.position = subscription.pageIndex > 0
					? { at: conversationCursor(page.startCursor) }
					: {};
			}

			subscribePage();
		} catch (error) {
			console.error("[CONVERSATION-V2] Failed to load conversations page:", error);
			finishLoading(-1);
		}

		return ready;
	};

	const loadFirstPage = async (conversationState, limit = currentLimit) =>
		(await showPage(conversationState, 0, limit)) >= 0;

	const loadNextPage = async (conversationState = currentState, limit = currentLimit) => {
		const nav = pageNav[conversationState];
		const boundary = nav.lastCursor ?? pageSubscription?.position.before ?? pageSubscription?.position.through;

		if (pageLoadInFlight || !boundary) return false;

		const position = !nav.lastCursor && pageSubscription?.position.before
			? { at: boundary }
			: { after: boundary };

		return (await showPage(conversationState, nav.pageIndex + 1, limit, position)) >= 0;
	};

	const loadPrevPage = async (conversationState = currentState, limit = currentLimit) => {
		const nav = pageNav[conversationState];
		const boundary = nav.firstCursor ?? pageSubscription?.position.at ?? pageSubscription?.position.after ??
			pageSubscription?.position.before ?? pageSubscription?.position.through;

		if (pageLoadInFlight || nav.pageIndex === 0 || !boundary) return false;

		const position = !nav.firstCursor && pageSubscription?.position.after
			? { through: boundary }
			: { before: boundary };

		return (await showPage(conversationState, nav.pageIndex - 1, limit,
			nav.pageIndex === 1 ? {} : position)) >= 0;
	};

	const showPageContaining = async (conversationState, sid, limit = currentLimit) => {
		if (pageSubscription?.seeded && pageSubscription.conversationState === conversationState &&
			pageSubscription.limit === limit && pageSubscription.sids.includes(sid)) {
			return pageSubscription.pageIndex;
		}

		const pageIndex = await showPage(conversationState, 0, limit, {}, () =>
			fetchConversationPageOf(conversationState, sid, { limit }));

		return pageIndex >= 0 && pageSubscription?.seeded &&
			pageSubscription.conversationState === conversationState && pageSubscription.limit === limit &&
			pageSubscription.sids.includes(sid) ? pageIndex : -1;
	};

	const showLastPage = (conversationState = currentState, limit = currentLimit) =>
		showPage(conversationState, 0, limit, {}, () =>
			fetchConversationsLastPage(conversationState, { limit }));

	const subscribeToConversations = (conversationState, limit = currentLimit) =>
		loadFirstPage(conversationState, limit);

	const setupMessageSubscriptionWatcher = () => {
		if (messageWatcherUnsub) return;

		messageWatcherUnsub = store.subscribe((mutation) => {
			if (mutation.type === "conversation/setSingleConversation") {
				const conv = mutation.payload;

				subscribeToConversationMessages(conv?.sid);
			} else if (mutation.type === "conversation/resetSelectedConversation" ||
				mutation.type === "conversation/release") {
				unsubscribeFromConversationMessages();
			}
		});
	};

	// ---- Lifecycle ----

	const bootstrap = async () => {
		console.debug("[CONVERSATION-V2] bootstrap");

		toggleState(INITIALIZING);

		ensureUnreadSubscription("active");
		setupMessageSubscriptionWatcher();
		toggleState(CONNECTED);

		if (pageSubscription) {
			const { conversationState, pageIndex, limit, position } = pageSubscription;

			showPage(conversationState, pageIndex, limit, position);
		}
	};

	const cleanup = () => {
		console.debug("[CONVERSATION-V2] cleanup");
		// Invalidate any in-flight page/count request so its result cannot commit
		// after the store/map have been released.
		loadGeneration++;
		unsubscribeFromConversations();
		currentState = "active";
		currentLimit = 20;
		pageNav.active = { pageIndex: 0, firstCursor: null, lastCursor: null };
		pageNav.inactive = { pageIndex: 0, firstCursor: null, lastCursor: null };
		unreadListenerOpen.active = false;
		unreadListenerOpen.inactive = false;

		for (const state of ["active", "inactive"]) {
			unsubscribeCollection(`${realtimeKey(state)}:unread`);
		}

		unsubscribeFromConversationMessages();

		if (messageWatcherUnsub) {
			messageWatcherUnsub();
			messageWatcherUnsub = null;
		}

		conversationsMap.clear();

		store.commit("conversation/release");
	};

	return {
		bootstrap,
		toggleState,
		cleanup,
		subscribeToConversations,
		unsubscribeFromConversations,
		loadFirstPage,
		loadNextPage,
		loadPrevPage,
		showLastPage,
		showPageContaining
	};
};

export { ConversationMessage };

export default useConversation;
